import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

type AuthStatus = 'idle' | 'anonymous' | 'authenticated';
type SessionState = 'resolving' | 'anonymous' | 'authenticated';

interface AuthState {
  hasHydrated: boolean;
  sessionState: SessionState;
  isReady: boolean;
  status: AuthStatus;
  token: string | null; // GH access token - single source of truth
  userId: string | null;
  isLoginSynced: boolean;
  isAuthSettling: boolean;
  setHasHydrated: (hydrated: boolean) => void;
  beginSessionResolution: () => void;
  resolveAuthenticatedSession: (params: { token: string; userId: string }) => void;
  resolveAnonymousSession: () => void;
  setReady: (userId?: string) => void; // compatibility shim for existing selectors/tests
  setGhAuth: (params: { token?: string | null; status?: AuthStatus }) => void;
  setLoginSyncedUser: (params: { userId?: string | null }) => void;
  markLoginUnsynced: () => void;
  clearAuth: () => void;
  setAuthSettling: (settling: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    set => ({
      hasHydrated: false,
      sessionState: 'resolving',
      isReady: false,
      status: 'idle',
      token: null,
      userId: null,
      isLoginSynced: false,
      isAuthSettling: false,
      setHasHydrated: hydrated => {
        set(state => ({
          ...state,
          hasHydrated: hydrated,
        }));
      },
      beginSessionResolution: () => {
        set(state => ({
          ...state,
          sessionState: 'resolving',
          isReady: false,
          isAuthSettling: false,
        }));
      },
      resolveAuthenticatedSession: ({ token, userId }) => {
        set({
          sessionState: 'authenticated',
          isReady: true,
          status: 'authenticated',
          token,
          userId,
          isLoginSynced: true,
          isAuthSettling: false,
        });
      },
      resolveAnonymousSession: () => {
        set({
          sessionState: 'anonymous',
          isReady: true,
          status: 'anonymous',
          token: null,
          userId: null,
          isLoginSynced: false,
          isAuthSettling: false,
        });
      },
      setReady: (userId?: string) => {
        const resolvedUserId = userId ?? null;
        set(state => ({
          sessionState:
            state.status === 'idle'
              ? resolvedUserId || state.token
                ? resolvedUserId
                  ? 'authenticated'
                  : 'resolving'
                : 'anonymous'
              : resolvedUserId
                ? 'authenticated'
                : state.token
                  ? 'resolving'
                  : 'anonymous',
          isReady: true,
          status:
            state.status === 'idle'
              ? resolvedUserId || state.token
                ? 'authenticated'
                : 'anonymous'
              : state.status,
          token: state.token,
          userId: resolvedUserId,
          isLoginSynced: state.isLoginSynced,
        }));
      },
      setGhAuth: ({ token, status }) => {
        const nextToken = token ?? null;
        const nextStatus = status ?? (nextToken ? 'authenticated' : 'anonymous');

        set(state => {
          if (nextStatus === 'anonymous' || !nextToken) {
            return {
              sessionState: 'anonymous' as const,
              isReady: true,
              status: 'anonymous' as const,
              token: null,
              userId: null,
              isLoginSynced: false,
              isAuthSettling: false,
            };
          }

          return {
            sessionState: 'resolving' as const,
            isReady: true,
            status: nextStatus,
            token: nextToken,
            userId: state.userId,
            isLoginSynced: state.isLoginSynced,
          };
        });
      },
      setLoginSyncedUser: ({ userId }) => {
        const nextUserId = userId ?? null;

        set(state => ({
          sessionState:
            state.token && nextUserId ? 'authenticated' : state.token ? 'resolving' : 'anonymous',
          isReady: true,
          status: state.token ? 'authenticated' : state.status,
          token: state.token,
          userId: nextUserId,
          isLoginSynced: !!state.token && !!nextUserId,
        }));
      },
      markLoginUnsynced: () => {
        set(state => ({
          ...state,
          sessionState: state.token ? 'resolving' : state.sessionState,
          isLoginSynced: false,
        }));
      },
      clearAuth: () => {
        set({
          sessionState: 'anonymous',
          isReady: true,
          status: 'anonymous',
          token: null,
          userId: null,
          isLoginSynced: false,
          isAuthSettling: false,
        });
      },
      setAuthSettling: (settling: boolean) => {
        set(state => ({
          ...state,
          isAuthSettling: settling,
        }));
      },
    }),
    {
      name: 'dreamteam-auth-store',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => state => {
        state?.setHasHydrated(true);
      },
      partialize: state => ({
        status: state.status,
        token: state.token,
        userId: state.userId,
        isLoginSynced: state.isLoginSynced,
      }),
    }
  )
);
